import { GalleryItemType } from '@/types/galleries'
import type { GalleryImage, ImageVariant, OrganizerGalleryItem } from '@/types/galleries'

export type ImageFormat = 'avif' | 'webp' | 'jpeg'

/** srcset string for one format built from whatever variants carry that format. */
export function variantSrcset(variants: ImageVariant[], format: ImageFormat): string | null {
  const parts = variants.flatMap((variant) => {
    const url = variant.sources[format]
    return url === undefined ? [] : [`${url} ${variant.width}w`]
  })
  return parts.length > 0 ? parts.join(', ') : null
}

/** Picks the variant closest to the wanted name, falling back to the smallest available one. */
export function pickVariant(image: GalleryImage, preferred: ImageVariant['name']): ImageVariant | null {
  return (
    image.variants.find((v) => v.name === preferred) ??
    [...image.variants].sort((a, b) => a.width - b.width)[0] ??
    null
  )
}

/** Fallback <img> URL: jpeg first, then webp, then avif. */
export function fallbackUrl(variant: ImageVariant): string | null {
  return variant.sources.jpeg ?? variant.sources.webp ?? variant.sources.avif ?? null
}

/** The image that represents an item in the grid, or null when none can be shown yet. */
export function itemPreviewImage(item: OrganizerGalleryItem): GalleryImage | null {
  if (item.type === GalleryItemType.Image) return item.image
  if (item.type === GalleryItemType.Video) return item.video?.poster ?? null
  return item.external_video?.thumbnail ?? null
}

export function itemDisplayName(item: OrganizerGalleryItem): string {
  if (item.type === GalleryItemType.ExternalVideo) {
    return item.external_video?.title ?? item.external_video?.watch_url ?? ''
  }
  return item.original_filename ?? ''
}
